import { Clock, Copy, RotateCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface PendingInviteItem {
  id: string;
  email: string;
  role: string | null;
  expiresAt?: string | Date | null;
  token?: string | null;
}

export function PendingInvitesCard({
  invites,
  onResend,
  isResending,
  onRevoke,
}: {
  invites: PendingInviteItem[];
  onResend: (inviteId: string) => void;
  isResending: boolean;
  onRevoke: (inviteId: string) => void;
}) {
  if (invites.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">
          Pending Invitations
        </CardTitle>
        <CardDescription className="text-xs">
          Invitations sent but not yet accepted.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.map((invite) => (
              <TableRow key={invite.id}>
                <TableCell className="text-sm font-medium">
                  {invite.email}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="secondary"
                    className="rounded-sm text-xs capitalize font-normal"
                  >
                    {invite.role === "owner" ? "Owner" : "Member"}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {invite.expiresAt
                      ? new Date(invite.expiresAt).toLocaleDateString()
                      : "7 days"}
                  </span>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <SimpleTooltip content="Resend invite">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      aria-label="Resend invite"
                      disabled={isResending}
                      onClick={() => onResend(invite.id)}
                    >
                      <RotateCw
                        className={cn(
                          "h-3.5 w-3.5",
                          isResending && "animate-spin",
                        )}
                      />
                    </Button>
                  </SimpleTooltip>
                  <SimpleTooltip content="Copy invite link">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      aria-label="Copy invite link"
                      onClick={() => {
                        if (invite.token) {
                          const link = `${window.location.origin}/invite/${invite.token}`;
                          navigator.clipboard.writeText(link);
                          toast.info("Invite link copied to clipboard");
                        } else {
                          toast.error(
                            "Invite token unavailable. Please resend the invite.",
                          );
                        }
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </SimpleTooltip>
                  <SimpleTooltip content="Revoke invite">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      aria-label="Revoke invite"
                      onClick={() => onRevoke(invite.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </SimpleTooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
